<template>
	<div>
		<div class="vx-card p-6">
			<h4 class="mb-6">Listes de Backlinks</h4>

			<!-- Delete Confirmation Popup -->
			<DeletePopup
				:show="showDeletePopup"
				@close-popup="showDeletePopup = false"
				@oui="deleteList(listToDelete)"
			/>

			<!-- Loading State -->
			<div v-if="loading" class="text-center py-6">
				<vs-loading active />
			</div>

			<!-- Lists Table -->
			<vs-table
				v-else
				:data="backlinkLists"
				stripe
				noDataText="Aucune liste disponible"
			>
				<template slot="thead">
					<vs-th>Nom de la liste</vs-th>
					<vs-th>Société</vs-th>
					<!-- <vs-th>Nombre de backlinks</vs-th> -->
					<vs-th>Date de création</vs-th>
					<vs-th>Actions</vs-th>
				</template>

				<template slot-scope="{ data }">
					<tbody v-for="(list, indexList) in data" :key="indexList">
						<vs-tr :data="list">
							<vs-td>
								<span
									@click.stop="event.stopPropagation()"
									@click="navigateToDetails(list.id)"
									class="hvr-underline hvr-underline-out"
									>{{ list.name }}
								</span>
							</vs-td>

							<vs-td>
								{{ list.society_name || "-" }}
							</vs-td>

							<!-- <vs-td>
								<vs-chip color="primary">
									{{ list.backlinks_count || 0 }}
								</vs-chip>
							</vs-td> -->

							<vs-td>
								{{ formatDate(list.created_date) }}
							</vs-td>

							<vs-td>
								<div class="flex space-x-2">
									<vs-button
										size="medium"
										color="primary"
										type="flat"
										icon-pack="feather"
										icon="icon-eye"
										@click="navigateToDetails(list.id)"
									>
									</vs-button>
									<vs-button
										size="medium"
										color="danger"
										type="flat"
										icon-pack="feather"
										icon="icon-trash-2"
										@click.stop="confirmDelete(list)"
									>
									</vs-button>
								</div>
							</vs-td>
						</vs-tr>
					</tbody>
				</template>
			</vs-table>

			<!-- Pagination -->
			<div v-if="totalPages > 1" class="mt-6 flex justify-center">
				<vs-pagination
					:total="totalPages"
					v-model="currentPage"
					@change="fetchBacklinkLists($event)"
				>
				</vs-pagination>
			</div>

			<!-- Summary Info -->
			<div
				v-if="!loading && totalCount > 0"
				class="mt-4 text-center text-gray-600"
			>
				Affichage de {{ backlinkLists.length }} liste(s) sur {{ totalCount }} au
				total
			</div>
		</div>
	</div>
</template>

<script>
import { Backlinks } from "@/entities/Backlinks";
import utilsMixin from "@/mixins/utilsMixin";
import moment from "moment";
import DeletePopup from "@/components/popups/DeletePopup.vue";

export default {
	name: "BacklinksLists",
	mixins: [utilsMixin],
	components: {
		DeletePopup,
	},
	data() {
		return {
			backlinkLists: [],
			selected: [],
			loading: false,
			currentPage: 1,
			totalPages: 1,
			totalCount: 0,
			itemsPerPage: 20,
			societyFilter: null,
			showDeletePopup: false,
			listToDelete: null,
		};
	},
	mounted() {
		this.fetchBacklinkLists();
	},
	methods: {
		async fetchBacklinkLists(page = 1) {
			try {
				this.loading = true;
				this.currentPage = page;

				// Use getAllLists which calls /backlinks/list API
				const response = await Backlinks.getAllLists();

				// Handle the response structure
				if (response.success) {
					// Apply pagination on the frontend since getAllLists returns all data
					const allLists = response.data || [];
					const startIndex = (this.currentPage - 1) * this.itemsPerPage;
					const endIndex = startIndex + this.itemsPerPage;

					this.backlinkLists = allLists.slice(startIndex, endIndex);
					this.totalCount = allLists.length;
					this.totalPages = Math.ceil(allLists.length / this.itemsPerPage);

					// Map the count properly
					this.backlinkLists = this.backlinkLists.map((list) => ({
						...list,
						backlinks_count: list.item_count || list.items_count || 0,
					}));
				} else {
					// Fallback to empty data
					this.backlinkLists = [];
				}
			} catch (error) {
				console.error("Error fetching backlink lists:", error);
				this.$vs.notify({
					title: "Erreur",
					text: "Impossible de charger les listes",
					color: "danger",
					position: "top-right",
				});
				this.backlinkLists = [];
			} finally {
				this.loading = false;
			}
		},
		navigateToDetails(listId) {
			// Navigate to the detail page with the list ID as a parameter
			this.$router.push({
				name: "backlinks-details",
				params: { id: listId },
			});
		},

		formatDate(date) {
			return date ? moment(date).format("DD/MM/YYYY") : "-";
		},

		formatNumber(num) {
			if (!num) return "0";
			return new Intl.NumberFormat("fr-FR").format(num);
		},

		formatNumberWithSpaces(num) {
			if (!num && num !== 0) return "0";
			return new Intl.NumberFormat("fr-FR").format(num);
		},

		getBqsColor(score) {
			const value = 101 - Number(score);
			if (value === 0 || isNaN(value)) return "#C3C3C3";
			return this.gradientColor(value);
		},

		getCategoryArray(categoryString) {
			if (!categoryString) return [];
			return categoryString.split(",").filter((cat) => cat && cat.trim());
		},

		confirmDelete(list) {
			this.listToDelete = list.id;
			this.showDeletePopup = true;
		},

		async deleteList(listId) {
			try {
				this.showDeletePopup = false;

				await Backlinks.deleteList(listId);

				this.$vs.notify({
					title: "Succès",
					text: "Liste supprimée avec succès",
					color: "success",
					position: "top-right",
				});

				// Refresh the list
				this.fetchBacklinkLists(this.currentPage);
			} catch (error) {
				console.error("Error deleting list:", error);
				this.$vs.notify({
					title: "Erreur",
					text: "Impossible de supprimer la liste",
					color: "danger",
					position: "top-right",
				});
			} finally {
				this.listToDelete = null;
			}
		},
	},
};
</script>

<style scoped>
.list-name-link:hover {
	opacity: 0.8;
}

.cursor-pointer {
	cursor: pointer;
}

.text-primary {
	color: #7367f0;
	text-decoration: none;
}

.text-primary:hover {
	text-decoration: underline;
}

.text-right {
	text-align: right !important;
}

.pdl0 {
	padding-left: 0 !important;
}

.w-full {
	width: 100%;
}

.mx-0 {
	margin-left: 0;
	margin-right: 0;
}

.category-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	align-items: center;
}
</style>